import usePendingState from '../../Hooks/usePendingState';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Select from 'react-select';

import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useNotification } from '../../Contexts/NotificationContext';
import Loading from '../Loading';

const FormAddSeller = () => {
    const { setNotification } = useNotification();
    const [loading, setLoading] = useState(true);
    const [states, setStates] = useState([]);
    const [cities, setCities] = useState([]);
    const [sellerData, setSellerData] = useState({
        nome: '',
        estado: '',
        cidade: '',
    });

    const [sellerErrorData, setSellerErrorData] = useState({
        nome: [],
        estado: [],
        cidade: [],
    });

    const [formLoading, setFormLoading, formPendingRef] = usePendingState();

    const navigate = useNavigate();
    const accessToken = localStorage.getItem('access_token');
    const baseUrl = process.env.REACT_APP_API_URL;

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setSellerData((prevData) => ({
            ...prevData,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    const handleStateChange = async (selectedOption) => {
        setSellerData((prevData) => ({
            ...prevData,
            estado: selectedOption.value,
            cidade: '',
        }));
        setSellerErrorData((prevData) => ({
            ...prevData,
            estado: [],
            cidade: [],
        }));

        try {
            const response = await fetch(`${baseUrl}/manager/estados/${selectedOption.value}/cidades/`, {
                method: 'GET',
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                    'Content-Type': 'application/json',
                },
            });
            if (response.ok) {
                const data = await response.json();
                setCities(data.cidades);
            } else {
                setNotification({
                    type: 'error',
                    message: 'Erro ao buscar cidades',
                    show: true,
                });
            }
        } catch (error) {
            setNotification({
                type: 'error',
                message: 'Erro na requisição de cidades.',
                show: true,
            });
        }
    };

    const handleCityChange = (selectedOption) => {
        setSellerData((prevData) => ({
            ...prevData,
            cidade: selectedOption.value,
        }));
        setSellerErrorData((prevData) => ({
            ...prevData,
            cidade: [],
        }));
    };

    useEffect(() => {
        const fetchContent = async () => {
            try {
                const statesResponse = await fetch(`${baseUrl}/manager/estados/`, {
                    method: 'GET',
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                        'Content-Type': 'application/json',
                    },
                });

                if (statesResponse.ok) {
                    const statesData = await statesResponse.json();
                    setStates(statesData.estados);
                } else {
                    setNotification({
                        type: 'error',
                        message: 'Erro ao buscar estados.',
                        show: true,
                    });
                }
            } catch (error) {
                setNotification({
                    type: 'error',
                    message: 'Erro na requisição de estados',
                    show: true,
                });
            } finally {
                setLoading(false);
            }
        };
        fetchContent();
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (formPendingRef.current) return;
        setFormLoading(true);

        try {
            const response = await fetch(`${baseUrl}/manager/representantes/novo/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${accessToken}`,
                },
                body: JSON.stringify(sellerData),
            });
            if (response.ok) {
                setNotification({
                    type: 'success',
                    message: 'Representante adicionado com sucesso!',
                    show: true,
                });
                navigate('/manager/representantes');
            } else {
                if (response.status === 422) {
                    const errors = await response.json();
                    setSellerErrorData(errors);
                } else {
                    throw new Error('Erro ao adicionar novo representante.');
                }
            }
        } catch (error) {
            setNotification({
                type: 'error',
                message: error.message,
                show: true,
            });
        } finally {
            setFormLoading(false);
        }
    };

    if (loading)
        return (
            <div className="mx-auto py-20 flex justify-center">
                <div className="h-16 w-16 animate-spin rounded-full border-4 border-solid border-primary border-t-transparent"></div>
            </div>
        );

    return (
        <>
            <Link
                to="/manager/representantes"
                className="bg-slate-100 px-4 py-1.5 rounded border border-slate-300 float-right ml-2 transition-all hover:bg-slate-200"
            >
                <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
                Voltar
            </Link>

            <h4 className="mb-6 text-xl font-bold text-black">Adicionar representante</h4>

            <div className="flex flex-col">
                <form onSubmit={handleSubmit} className="max-w-5xl pb-10">
                    <div className="mb-6 2xl:mb-10">
                        <div className="mb-3 flex gap-5 flex-col lg:flex-row">
                            <div className="w-full lg:w-3/4">
                                <label className="block text-sm font-medium mb-1">Nome</label>
                                <input
                                    type="text"
                                    name="nome"
                                    value={sellerData.nome}
                                    onClick={() => setSellerErrorData((prevData) => ({ ...prevData, nome: [] }))}
                                    onChange={handleChange}
                                    className="w-full px-3 py-1.5 2xl:py-2 border border-gray-300 rounded-md outline-none focus:border-secondary focus-visible:shadow-none"
                                />
                                {sellerErrorData.nome && sellerErrorData.nome[0] && (
                                    <span className="flex justify-between border-b border-red-500 text-xs 2xl:text-sm text-red-500 tracking-tight 2xl:tracking-normal mt-1 pb-1 pl-2">
                                        {sellerErrorData.nome[0]}{' '}
                                        <span
                                            onClick={() => setSellerErrorData((prevData) => ({ ...prevData, nome: [] }))}
                                            className="rotate-45 -my-2 p-2 cursor-pointer"
                                        >
                                            +
                                        </span>
                                    </span>
                                )}
                            </div>
                        </div>

                        <div className="mb-3 flex gap-5 flex-col lg:flex-row w-full lg:w-3/4">
                            <div className="w-1/2">
                                <label className="block text-sm font-medium">Estado</label>
                                <Select
                                    name="estado"
                                    options={states}
                                    value={states.find((option) => option.value === sellerData.estado) || null}
                                    onClick={() => setSellerErrorData((prevData) => ({ ...prevData, estado: [] }))}
                                    onChange={handleStateChange}
                                    placeholder="Selecione um estado..."
                                    classNamePrefix="admin-select"
                                    className="seller-select"
                                />
                                {sellerErrorData.estado && sellerErrorData.estado[0] && (
                                    <span className="flex justify-between border-b border-red-500 text-xs 2xl:text-sm text-red-500 tracking-tight 2xl:tracking-normal mt-1 pb-1 pl-2">
                                        {sellerErrorData.estado[0]}{' '}
                                        <span
                                            onClick={() => setSellerErrorData((prevData) => ({ ...prevData, estado: [] }))}
                                            className="rotate-45 -my-2 p-2 cursor-pointer"
                                        >
                                            +
                                        </span>
                                    </span>
                                )}
                            </div>
                            <div className="w-1/2">
                                <label className="block text-sm font-medium">Cidade</label>
                                <Select
                                    name="cidade"
                                    options={cities}
                                    value={cities.find((option) => option.value === sellerData.cidade) || null}
                                    onClick={() => setSellerErrorData((prevData) => ({ ...prevData, cidade: [] }))}
                                    onChange={handleCityChange}
                                    placeholder={!sellerData.estado ? 'Selecione primeiro um estado' : 'Selecione uma cidade...'}
                                    classNamePrefix="admin-select"
                                    className="seller-select"
                                    isDisabled={!sellerData.estado}
                                />
                                {sellerErrorData.cidade && sellerErrorData.cidade[0] && (
                                    <span className="flex justify-between border-b border-red-500 text-xs 2xl:text-sm text-red-500 tracking-tight 2xl:tracking-normal mt-1 pb-1 pl-2">
                                        {sellerErrorData.cidade[0]}{' '}
                                        <span
                                            onClick={() => setSellerErrorData((prevData) => ({ ...prevData, cidade: [] }))}
                                            className="rotate-45 -my-2 p-2 cursor-pointer"
                                        >
                                            +
                                        </span>
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>

                    <button
                        type="submit"
                        disabled={formLoading}
                        aria-busy={formLoading}
                        className="w-full lg:w-3/4 bg-secondary text-white py-2 px-4 rounded-md hover:bg-opacity-80 disabled:bg-opacity-80 disabled:cursor-not-allowed"
                    >
                        {!formLoading ? 'Salvar' : <Loading />}
                    </button>
                </form>
            </div>
        </>
    );
};

export default FormAddSeller;
